import { PageRenderProfile } from '@/types/enums';

const LOGROCKET_APP_ID = import.meta.env.VITE_LOGROCKET_APP_ID;

type LogRocketSdk = typeof import('logrocket');
type LogRocketImport = LogRocketSdk & { default?: LogRocketSdk };
type CaptureExceptionOptions = Parameters<LogRocketSdk['captureException']>[1];
type CaptureMessageOptions = Parameters<LogRocketSdk['captureMessage']>[1];
type IdentifyTraits = Record<string, string | number | boolean>;

let isInitialized = false;
let logRocketPromise: Promise<LogRocketSdk | null> | null = null;

function shouldLoadLogRocket(): boolean {
  if (
    !LOGROCKET_APP_ID ||
    (typeof document !== 'undefined' &&
      document.documentElement.dataset.renderProfile === PageRenderProfile.Print)
  ) {
    return false;
  }

  const enableInDev = import.meta.env.VITE_LOGROCKET_ENABLE_IN_DEV === 'true';

  return !import.meta.env.DEV || enableInDev;
}

function initialize(logRocket: LogRocketSdk): void {
  const appId = LOGROCKET_APP_ID;

  if (isInitialized || !appId) {
    return;
  }

  logRocket.init(appId, {
    release: import.meta.env.VITE_APP_VERSION || undefined,
    console: {
      isEnabled: true,
      shouldAggregateConsoleErrors: true
    },
    network: {
      requestSanitizer: (request) => {
        if (request.headers?.Authorization) {
          request.headers.Authorization = '[REDACTED]';
        }
        if (request.headers?.['X-CSRF-TOKEN']) {
          request.headers['X-CSRF-TOKEN'] = '[REDACTED]';
        }
        return request;
      },
      responseSanitizer: (response) => {
        return response;
      }
    }
  });

  isInitialized = true;
}

async function loadLogRocket(): Promise<LogRocketSdk | null> {
  if (!shouldLoadLogRocket()) {
    return null;
  }

  logRocketPromise ??= import('logrocket')
    .then((module) => {
      const logRocketModule = module as LogRocketImport;
      const logRocket = logRocketModule.default ?? logRocketModule;

      initialize(logRocket);

      return logRocket;
    })
    .catch(() => {
      logRocketPromise = null;
      return null;
    });

  return logRocketPromise;
}

export const LogRocket = {
  captureException(error: Error, options?: CaptureExceptionOptions): void {
    void loadLogRocket().then((logRocket) => {
      logRocket?.captureException(error, options);
    });
  },

  captureMessage(message: string, options?: CaptureMessageOptions): void {
    void loadLogRocket().then((logRocket) => {
      logRocket?.captureMessage(message, options);
    });
  },

  identify(uid: string, traits?: IdentifyTraits): void {
    void loadLogRocket().then((logRocket) => {
      logRocket?.identify(uid, traits);
    });
  }
};
